import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle2, Instagram, Mail, MapPin, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { projectsData } from '@/data/projectsData';
import { trackEvent } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = { name: '', email: '', phone: '', project_interest: '', message: '' };

const Contact = () => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        const selectedProject = projectsData.find((project) => project.name === formData.project_interest);
        trackEvent('general_inquiry_submit', { project_id: selectedProject?.id || null });
        toast.success('Inquiry received', { description: 'The Next Leap team will reach out within 24 hours.' });
        setFormData(initialForm);
      }
    } catch (error) {
      toast.error('Could not send inquiry', { description: 'Please try again or call +91 70393 10297.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-[#F5F5F5] py-24 sm:py-32" data-testid="contact-section">
      <div className="absolute bottom-0 left-0 h-96 w-96 rounded-full bg-[#C5A880]/15 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.86fr_1.14fr] lg:items-start">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:sticky lg:top-32">
            <div className="mb-5 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Private inquiry</div>
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-[#0B1F3B] sm:text-4xl">Tell us what your next leap should own.</h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#0B1F3B]/70">Share your target project, budget, or investment brief. The directors’ team will respond with a precise next step.</p>

            <div className="mt-10 grid gap-4">
              <a href="tel:+917039310297" className="group flex items-center gap-4 rounded-3xl border border-[#0B1F3B]/10 bg-white p-5 transition-all hover:-translate-y-1 hover:border-[#C5A880]/50" data-testid="contact-phone-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0B1F3B] text-white"><Phone className="h-5 w-5" /></span>
                <span><span className="block font-tech text-[10px] uppercase tracking-[0.2em] text-[#0B1F3B]/50">Direct line</span><span className="font-semibold">+91 70393 10297</span></span>
              </a>
              <a href="mailto:vikrant.pawar@nextleapinc.com" className="group flex items-center gap-4 rounded-3xl border border-[#0B1F3B]/10 bg-white p-5 transition-all hover:-translate-y-1 hover:border-[#C5A880]/50" data-testid="contact-email-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#C5A880] text-[#0B1F3B]"><Mail className="h-5 w-5" /></span>
                <span><span className="block font-tech text-[10px] uppercase tracking-[0.2em] text-[#0B1F3B]/50">Email</span><span className="break-all font-semibold">vikrant.pawar@nextleapinc.com</span></span>
              </a>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-3xl border border-[#0B1F3B]/10 bg-white p-5"><MapPin className="mb-4 h-5 w-5 text-[#C5A880]" /><div className="font-tech text-[10px] uppercase tracking-[0.18em] text-[#0B1F3B]/50">Coverage</div><div className="mt-1 font-semibold">Pan-India</div></div>
                <a href="https://www.instagram.com/nextlead_inc?igsi=cHFpdGRscTl2eWQ1" target="_blank" rel="noreferrer" className="rounded-3xl border border-[#0B1F3B]/10 bg-white p-5 transition-transform hover:-translate-y-1" data-testid="contact-instagram-card"><Instagram className="mb-4 h-5 w-5 text-[#C5A880]" /><div className="font-tech text-[10px] uppercase tracking-[0.18em] text-[#0B1F3B]/50">Instagram</div><div className="mt-1 font-semibold">@nextlead_inc</div></a>
              </div>
            </div>
          </motion.div>

          <motion.form initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.75, delay: 0.1 }} onSubmit={handleSubmit} className="rounded-[2rem] border border-[#0B1F3B]/10 bg-white p-6 shadow-[0_30px_90px_rgba(6,16,30,0.11)] sm:p-8" data-testid="contact-form">
            <div className="mb-8 flex items-center justify-between border-b border-[#0B1F3B]/10 pb-6">
              <div>
                <div className="font-tech text-[11px] uppercase tracking-[0.22em] text-[#C5A880]">Inquiry engine</div>
                <h3 className="mt-2 font-display text-3xl font-semibold text-[#0B1F3B]">Start the conversation</h3>
              </div>
              <CheckCircle2 className="h-8 w-8 text-[#1E7B54]" />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="grid gap-2 text-sm font-semibold text-[#0B1F3B]">Full name
                <Input data-testid="contact-form-name-input" name="name" value={formData.name} onChange={handleChange} required placeholder="Your name" className="h-[52px] rounded-2xl border-[#0B1F3B]/10 bg-[#F5F5F5] px-4" />
              </label>
              <label className="grid gap-2 text-sm font-semibold text-[#0B1F3B]">Phone / WhatsApp
                <Input data-testid="contact-form-phone-input" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+91" className="h-[52px] rounded-2xl border-[#0B1F3B]/10 bg-[#F5F5F5] px-4" />
              </label>
              <label className="grid gap-2 text-sm font-semibold text-[#0B1F3B]">Email address
                <Input data-testid="contact-form-email-input" type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="you@example.com" className="h-[52px] rounded-2xl border-[#0B1F3B]/10 bg-[#F5F5F5] px-4" />
              </label>
              <label className="grid gap-2 text-sm font-semibold text-[#0B1F3B]">Target mandate
                <select data-testid="contact-form-project-select" name="project_interest" value={formData.project_interest} onChange={handleChange} className="h-[52px] rounded-2xl border border-[#0B1F3B]/10 bg-[#F5F5F5] px-4 text-sm font-medium text-[#0B1F3B]">
                  <option value="">Open to guidance</option>
                  {projectsData.map((project) => <option key={project.id} value={project.name}>{project.name}</option>)}
                </select>
              </label>
            </div>

            <label className="mt-5 grid gap-2 text-sm font-semibold text-[#0B1F3B]">Investment brief
              <Textarea data-testid="contact-form-message-input" name="message" value={formData.message} onChange={handleChange} required rows={6} placeholder="Budget, preferred location, timeline, or questions..." className="resize-none rounded-2xl border-[#0B1F3B]/10 bg-[#F5F5F5] px-4 py-4" />
            </label>

            <Button type="submit" disabled={isSubmitting} data-testid="contact-form-submit-btn" className="mt-7 h-14 w-full rounded-full bg-[#0B1F3B] text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#06101E] disabled:translate-y-0">
              {isSubmitting ? 'Sending inquiry...' : 'Send private inquiry'}
              <Send className="h-4 w-4" />
            </Button>
            <p className="mt-4 text-center font-tech text-[10px] uppercase tracking-[0.16em] text-[#0B1F3B]/40">Response target · under 24 hours</p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
